import React from 'react';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, SerbiaEInvoicingPilot } from '../constants';
import { registerTaxform } from '../taxform-store';
import { withViewTaxFormWithFilledEmptyTaxValue, getHelpPopupTopicFromTaxSchema, getPilotStatus, getTaxTypeLabel } from '../utils';
import { validateField, CustomValidateItems, ValidateItems } from '../validator';
import { useValidationI18n } from '../hooks/use-i18n';

const { TaxType, TaxLabelFormat, CountryCode } = Constants;
const { isPIBNumberValid, isRSLegalNumberValid } = TaxUtils;

const getSerbiaTaxSchema = ({ isSignupFlow, permissions }) => {
  const isSerbiaEInvoicingPilotOn = getPilotStatus(SerbiaEInvoicingPilot, permissions);
  const taxSchema = {
    name: 'Serbia Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.VatNumber]: {
        type: 'string',
        maxLength: 13,
        required: true,
        helpPopupTopic: isSignupFlow ? 'pop_BA_Signup_PIB' : 'pop_BA_Accounts_PIB',
      },
    },
  };
  if (isSerbiaEInvoicingPilotOn) {
    taxSchema.properties[TaxType.LegalIdentifier] = {
      type: 'string',
      maxLength: 8,
      required: true,
      helpPopupTopic: 'pop_BA_Accounts_SerbiaEInvoicing',
    };
  }
  return taxSchema;
};

const SerbiaTaxForm = (props) => {
  const validationI18n = useValidationI18n(props.i18n);
  const CustomizedFormGroup = props.showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const taxSchema = getSerbiaTaxSchema(props);
  const isSerbiaEInvoicingPilotOn = getPilotStatus(SerbiaEInvoicingPilot, props.permissions);

  return (
    <ValidationGroup
      validationProps={{
        schema: taxSchema,
        i18n: validationI18n,
        onValidate: props.onValidate,
        data: props.data,
      }}
    >
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.VatNumber, props.i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.RS })}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.VatNumber })}
      >
        <Input
          id={InputTaxFieldId('PIB')}
          validationProps={{
            i18n: validationI18n,
            field: TaxType.VatNumber,
            validator: value => validateField(
              value,
              {},
              {
                [CustomValidateItems.PIBRequired]: inputValue => inputValue.length === 0,
                [CustomValidateItems.PIBValidation]: inputValue => inputValue.length > 0 && !isPIBNumberValid(inputValue),
              },
              props.i18n
            ),
            alwaysShowError: props.alwaysShowError,
          }}
          disabled={props.disabled}
        />
        <InlineInfoAlert>{props.i18n.getString(_TL_('Failure to provide your Tax Identification Number may result in tax being applied to your invoice.'))}</InlineInfoAlert>
      </CustomizedFormGroup>
      {isSerbiaEInvoicingPilotOn && (
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.LegalIdentifier, props.i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.RS })}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.LegalIdentifier })}
      >
        <Input
          id={InputTaxFieldId('LegalNumber')}
          validationProps={{
            i18n: validationI18n,
            field: TaxType.LegalIdentifier,
            validator: value => validateField(
              value,
              { [ValidateItems.Required]: { isRequired: true } },
              { [CustomValidateItems.LegalNumberValidation]: inputValue => inputValue.length > 0 && !isRSLegalNumberValid(inputValue) },
              props.i18n
            ),
            alwaysShowError: props.alwaysShowError,
          }}
          disabled={props.disabled}
        />
      </CustomizedFormGroup>
      )}
    </ValidationGroup>
  );
};

SerbiaTaxForm.propTypes = TaxTypeFormPropTypes;

registerTaxform('/RS/', SerbiaTaxForm, withViewTaxFormWithFilledEmptyTaxValue({
  getTaxSchema: getSerbiaTaxSchema,
  countryCode: CountryCode.RS,
}));
